import { useQuery } from '@tanstack/react-query'
import { Input, Select } from 'antd'
import moment from 'moment'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import Header from '@components/header'
import Layout from '@components/Layout'
import { useDebounce } from '@hooks/useDebounce'
import { downloadExcel } from '@utils/downloadPdf'
import { fetchProductionGrossProfitReport, fetchDepartments } from '@utils/fetchData'
import { numberWithSpacesIntl } from '@utils/numberWithSpaces'

const GrossProfitReport = () => {
	const { t } = useTranslation()
	const formattedDate = moment().format('YYYY-MM-DD')

	const [startDate, setStartDate] = useState('')
	const [endDate, setEndDate] = useState('')
	const [docEntry, setDocEntry] = useState('')
	const [docNum, setDocNum] = useState('')
	const [department, setDepartment] = useState('')
	const [departmentName, setDepartmentName] = useState('')
	const [itemCode, setItemCode] = useState('')
	const [itemName, setItemName] = useState('')
	const [lineNum, setLineNum] = useState('')

	const debouncedDocEntry = useDebounce(docEntry, 500)
	const debouncedDocNum = useDebounce(docNum, 500)
	const debouncedDepartmentName = useDebounce(departmentName, 500)
	const debouncedItemCode = useDebounce(itemCode, 500)
	const debouncedItemName = useDebounce(itemName, 500)
	const debouncedLineNum = useDebounce(lineNum, 500)

	const [pageSize] = useState(20)
	const [page, setPage] = useState(0)
	const [loadingExcel, setLoadingExcel] = useState(false)

	const { data: departmentData = [] } = useQuery({
		queryKey: ['departments'],
		queryFn: fetchDepartments,
		keepPreviousData: true,
		refetchOnWindowFocus: false,
	})

	const { data = [], isLoading } = useQuery({
		queryKey: [
			'production-gross-profit-report',
			pageSize,
			page,
			startDate,
			endDate,
			debouncedDocEntry,
			debouncedDocNum,
			department,
			debouncedDepartmentName,
			debouncedItemCode,
			debouncedItemName,
			debouncedLineNum,
		],
		queryFn: fetchProductionGrossProfitReport,
		keepPreviousData: true,
		refetchOnWindowFocus: false,
	})

	const columns = [
		{
			title: t('docNum'),
			dataIndex: 'docNum',
			key: 'docNum',
		},
		{
			title: t('docDate'),
			dataIndex: 'docDate',
			key: 'docDate',
			render: (value) => (value ? moment(value).format('DD.MM.YYYY') : '-'),
		},

		{
			title: t('departmentName'),
			dataIndex: 'departmentName',
			key: 'departmentName',
		},
		// {
		// 	title: t('productLineNum'),
		// 	dataIndex: 'productLineNum',
		// 	key: 'productLineNum',
		// },

		{
			title: t('itemName'),
			dataIndex: 'itemName',
			key: 'itemName',
		},
		// {
		// 	title: t('quantity'),
		// 	dataIndex: 'u_PlannedQuantity',
		// 	key: 'u_PlannedQuantity',
		// 	render: (value) => numberWithSpacesIntl(value || 0),
		// },
		{
			title: t('kv.m'),
			dataIndex: 'quantity',
			key: 'quantity',
			render: (value) => numberWithSpacesIntl(value || 0),
		},
		// {
		// 	title: t('electricityCostPerQty'),
		// 	dataIndex: 'electricityCostPerQty',
		// 	key: 'electricityCostPerQty',
		// 	render: (value) => numberWithSpacesIntl(value || 0),
		// },
		// {
		// 	title: t('machineCostPerQty'),
		// 	dataIndex: 'machineCostPerQty',
		// 	key: 'machineCostPerQty',
		// 	render: (value) => numberWithSpacesIntl(value || 0),
		// },
		{
			title: t('priceBefDi'),
			dataIndex: 'priceBefDi',
			key: 'priceBefDi',
			render: (value) => numberWithSpacesIntl(value || 0),
		},
		{
			title: t('price'),
			dataIndex: 'price',
			key: 'price',
			render: (value) => numberWithSpacesIntl(value || 0),
		},
		{
			title: t('productSellingPrice'),
			dataIndex: 'productSellingPrice',
			key: 'productSellingPrice',
			render: (value) => numberWithSpacesIntl(value || 0),
		},
		{
			title: t('relatedServiceSellingPrice'),
			dataIndex: 'relatedServiceSellingPrice',
			key: 'relatedServiceSellingPrice',
			render: (value) => numberWithSpacesIntl(value || 0),
		},
		{
			title: t('rawSellingPrice'),
			dataIndex: 'rawSellingPrice',
			key: 'rawSellingPrice',
			render: (value) => numberWithSpacesIntl(value || 0),
		},
		{
			title: t('rawDocumentSellingPrice'),
			dataIndex: 'rawDocumentSellingPrice',
			key: 'rawDocumentSellingPrice',
			render: (value) => numberWithSpacesIntl(value || 0),
		},
		// {
		// 	title: t('documentTotal'),
		// 	dataIndex: 'documentTotal',
		// 	key: 'documentTotal',
		// 	render: (value) => numberWithSpacesIntl(value || 0),
		// },
		{
			title: t('sellingPrice'),
			dataIndex: 'sellingPrice',
			key: 'sellingPrice',
			render: (value) => numberWithSpacesIntl(value || 0),
		},
		{
			title: t('purchasingPrice'),
			dataIndex: 'purchasingPrice',
			key: 'purchasingPrice',
			render: (value) => numberWithSpacesIntl(value || 0),
		},
		{
			title: t('arendaCost'),
			dataIndex: 'arendaCost',
			key: 'arendaCost',
			render: (value) => numberWithSpacesIntl(value || 0),
		},
		{
			title: t('ishXaqqiCost'),
			dataIndex: 'ishXaqqiCost',
			key: 'ishXaqqiCost',
			render: (value) => numberWithSpacesIntl(value || 0),
		},
		{
			title: t('elektrCost'),
			dataIndex: 'elektrCost',
			key: 'elektrCost',
			render: (value) => numberWithSpacesIntl(value || 0),
		},
		{
			title: t('stanokCost'),
			dataIndex: 'stanokCost',
			key: 'stanokCost',
			render: (value) => numberWithSpacesIntl(value || 0),
		},
		{
			title: t('costs'),
			dataIndex: 'costs',
			key: 'costs',
			render: (value) => numberWithSpacesIntl(value || 0),
		},
		{
			title: t('workerSalaries'),
			dataIndex: 'workerSalaries',
			key: 'workerSalaries',
			render: (value) => numberWithSpacesIntl(value || 0),
		},
		{
			title: t('grossProfit'),
			dataIndex: 'grossProfit',
			key: 'grossProfit',
			render: (value) => numberWithSpacesIntl(value || 0),
		},
	]

	const handleNextPage = () => {
		if (data.length === pageSize) {
			setPage((prev) => prev + 1)
		}
	}

	const handlePreviousPage = () => {
		if (page > 0) {
			setPage((prev) => prev - 1)
		}
	}

	const handleDownloadExcel = () => {
		downloadExcel({
			url: 'api/reports/production-gross-profit-report/export',
			params: {
				startDate,
				endDate,
				docEntry: debouncedDocEntry,
				docNum: debouncedDocNum,
				department,
				departmentName: debouncedDepartmentName,
				itemCode: debouncedItemCode,
				itemName: debouncedItemName,
				lineNum: debouncedLineNum,
				skip: 0,
				pageSize: 1000,
			},
			fileName: t('prodGrossProfit'),
			onLoadingChange: setLoadingExcel,
		})
	}

	return (
		<Layout>
			<Header
				title={'prodGrossProfit'}
				columns={columns}
				fdata={data}
				loading={isLoading}
				hasMoreData={data.length === pageSize}
				currentPage={page + 1}
				handlePreviousPage={handlePreviousPage}
				handleNextPage={handleNextPage}
				formattedDate={formattedDate}
				loadingExcel={loadingExcel}
				handleDownloadExcel={handleDownloadExcel}
				isShowExcelBtn
				scrollX={3500}
				renderFilters={() => (
					<div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 items-end gap-4">
						<div className="flex flex-col gap-1">
							<p className="font-nunito">{t('startDate')}</p>
							<Input
								type="date"
								value={startDate}
								onChange={(e) => setStartDate(e.target.value)}
								placeholder={t('startDate')}
								allowClear
							/>
						</div>
						<div className="flex flex-col gap-1">
							<p className="font-nunito">{t('endDate')}</p>
							<Input
								type="date"
								value={endDate}
								onChange={(e) => setEndDate(e.target.value)}
								placeholder={t('endDate')}
								allowClear
							/>
						</div>
						<div className="flex flex-col gap-1">
							<p className="font-nunito">{t('docEntry')}</p>
							<Input
								value={docEntry}
								onChange={(e) => setDocEntry(e.target.value)}
								placeholder={t('docEntry')}
								allowClear
							/>
						</div>
						<div className="flex flex-col gap-1">
							<p className="font-nunito">{t('docNum')}</p>
							<Input
								value={docNum}
								onChange={(e) => setDocNum(e.target.value)}
								placeholder={t('docNum')}
								allowClear
							/>
						</div>
						<div className="flex flex-col gap-1">
							<p className="font-nunito">{t('department')}</p>
							<Select
								value={department || undefined}
								className="flex h-8 w-full items-center justify-center"
								style={{ color: 'black' }}
								onSelect={(val) => setDepartment(val)}
								options={departmentData.map((d) => ({
									label: d.name,
									value: d.code,
								}))}
								filterOption={(input, option) =>
									option.label.toLowerCase().includes(input.toLowerCase())
								}
								showSearch
								allowClear
								onClear={() => setDepartment('')}
							/>
						</div>

						<div className="flex flex-col gap-1">
							<p className="font-nunito">{t('itemCode')}</p>
							<Input
								value={itemCode}
								onChange={(e) => setItemCode(e.target.value)}
								placeholder={t('itemCode')}
								allowClear
							/>
						</div>
						<div className="flex flex-col gap-1">
							<p className="font-nunito">{t('itemName')}</p>
							<Input
								value={itemName}
								onChange={(e) => setItemName(e.target.value)}
								placeholder={t('itemName')}
								allowClear
							/>
						</div>
						{/* <div className="flex flex-col gap-1">
							<p className="font-nunito">{t('lineNum')}</p>
							<Input
								value={lineNum}
								onChange={(e) => setLineNum(e.target.value)}
								placeholder={t('lineNum')}
								allowClear
							/>
						</div> */}
					</div>
				)}
			/>
		</Layout>
	)
}

export default GrossProfitReport
